import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  HStack,
  Input,
  Select,
  Switch,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useClientContextStore } from '@/store/clientContext'
import { fetchClientOptionsList } from '@/lib/services/clientContextService'
import {
  createBusinessUnit,
  fetchBusinessUnit,
  fetchBusinessUnitTreeOptions,
  updateBusinessUnit,
} from '@/lib/services/businessUnitService'
import { businessUnitSchema, type BusinessUnitFormData } from '@/lib/validations/businessUnit'

interface BusinessUnitFormProps {
  businessUnitId?: number
  onSuccess?: () => void
  onCancel?: () => void
}

export function BusinessUnitForm({ businessUnitId, onSuccess, onCancel }: BusinessUnitFormProps) {
  const colors = useThemeColors()
  const { showError, showSuccess } = useToastNotification()
  const activeClientCode = useClientContextStore((s) => s.activeClientCode)
  const setActiveClient = useClientContextStore((s) => s.setActiveClient)
  const isEditMode = !!businessUnitId

  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedClientCode, setSelectedClientCode] = useState<string | null>(activeClientCode ?? null)
  const [clientOptions, setClientOptions] = useState<Array<{ id: number; code: string; name: string; label: string; database_name?: string }>>([])
  const [parentOptions, setParentOptions] = useState<Array<{ id: number; label: string }>>([])

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<BusinessUnitFormData>({
    resolver: zodResolver(businessUnitSchema),
    defaultValues: {
      parent_id: null,
      name: '',
      business_code: '',
      descriptions: '',
      active: true,
    },
  })

  const activeValue = watch('active')

  useEffect(() => {
    if (activeClientCode) setSelectedClientCode(activeClientCode)
  }, [activeClientCode])

  useEffect(() => {
    fetchClientOptionsList()
      .then((clients) =>
        setClientOptions(
          clients.map((c) => ({
            id: c.id,
            code: c.code,
            name: c.name,
            database_name: c.database_name,
            label: `${c.name} (${c.code})`,
          }))
        )
      )
      .catch(showError)
  }, [showError])

  useEffect(() => {
    if (!selectedClientCode) {
      setParentOptions([])
      setValue('parent_id', null)
      return
    }

    fetchBusinessUnitTreeOptions(selectedClientCode)
      .then((items) => {
        const filtered = isEditMode && businessUnitId ? items.filter((i) => i.id !== businessUnitId) : items
        setParentOptions(filtered)
      })
      .catch(() => setParentOptions([]))
  }, [selectedClientCode, isEditMode, businessUnitId, setValue])

  useEffect(() => {
    if (!isEditMode || !businessUnitId || !selectedClientCode) return

    setIsLoading(true)
    fetchBusinessUnit(businessUnitId)
      .then((unit) => {
        reset({
          parent_id: unit.parent_id ?? null,
          name: unit.name ?? '',
          business_code: unit.business_code ?? '',
          descriptions: unit.descriptions ?? '',
          active: unit.active === 'Inactive' ? false : unit.active !== false,
        })
      })
      .catch(showError)
      .finally(() => setIsLoading(false))
  }, [isEditMode, businessUnitId, selectedClientCode, reset, showError])

  const onSubmit = async (values: BusinessUnitFormData) => {
    const selectedClient = clientOptions.find((c) => c.code === selectedClientCode)
    if (!selectedClient) {
      showError(new Error('Please select Business Unit (Client) first'))
      return
    }

    setActiveClient({
      id: selectedClient.id,
      code: selectedClient.code,
      name: selectedClient.name,
      database_name: selectedClient.database_name,
    })

    setIsSubmitting(true)
    try {
      if (isEditMode && businessUnitId) {
        await updateBusinessUnit(businessUnitId, values, selectedClient.code)
        showSuccess('Success', 'Business unit updated successfully')
      } else {
        await createBusinessUnit(values, selectedClient.code)
        showSuccess('Success', 'Business unit created successfully')
      }
      onSuccess?.()
    } catch (error: unknown) {
      showError(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <Box py={8} textAlign="center">
        <Text color={colors.text}>Loading business unit...</Text>
      </Box>
    )
  }

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)}>
      <VStack spacing={4} align="stretch">
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Business Unit (Client) <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl>
            <Select
              placeholder="Select client"
              value={selectedClientCode ?? ''}
              onChange={(e) => {
                const code = e.target.value || null
                setSelectedClientCode(code)
                setValue('parent_id', null)
              }}
            >
              {clientOptions.map((client) => (
                <option key={client.id} value={client.code}>
                  {client.label}
                </option>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Parent Business Unit</FormLabel>
          <FormControl isInvalid={!!errors.parent_id}>
            <Select
              placeholder="None (top-level business unit)"
              {...register('parent_id', {
                setValueAs: (v) => (v === '' || v === undefined ? null : Number(v)),
              })}
              isDisabled={!selectedClientCode}
            >
              {parentOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </Select>
            <FormErrorMessage>{errors.parent_id?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Name <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.name}>
            <Input {...register('name')} placeholder="e.g. Consumer Electronics" />
            <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Business Code <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.business_code}>
            <Input {...register('business_code')} placeholder="e.g. CONSUMER_ELEC" />
            <FormErrorMessage>{errors.business_code?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Description</FormLabel>
          <FormControl isInvalid={!!errors.descriptions}>
            <Textarea rows={3} {...register('descriptions')} placeholder="Optional description" />
            <FormErrorMessage>{errors.descriptions?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Active</FormLabel>
          <FormControl>
            <HStack>
              <Switch isChecked={activeValue} onChange={(e) => setValue('active', e.target.checked)} />
              <Text fontSize="sm" color={colors.text}>{activeValue ? 'Active' : 'Inactive'}</Text>
            </HStack>
          </FormControl>
        </Grid>

        <HStack spacing={4} justify="flex-end" pt={4}>
          {onCancel && (
            <Button variant="outline" type="button" onClick={onCancel} isDisabled={isSubmitting}>
              Cancel
            </Button>
          )}
          <Button type="submit" colorScheme="blue" isLoading={isSubmitting} loadingText={isEditMode ? 'Saving...' : 'Creating...'}>
            {isEditMode ? 'Save' : 'Create Business Unit'}
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}

